import React from 'react'
import Chart from 'react-google-charts'
import { useChartTooltip } from './ChartTooltip'
import { useSelector } from 'react-redux'
import { getDisplayOptions, getYou, getShowWeather } from '../redux/options/selectors' 
import { getFastestPerCar, getFastestPerDay, getFastestPerGroup } from '../redux/data/selectors'
import { getLapColour } from '../tools/LapStatus'
import { ms2laptime, laptime2ms, time2seconds, getClockTicks } from '../tools/Time'
import ChartContainer from './ChartContainer'
import { LAPTIME_AXIS_WIDTH, getLaptimeAxis } from '../tools/ChartAxis'
import WeatherStrip, { WEATHER_HEIGHT } from './WeatherStrip'
import WeatherForecast from './WeatherForecast'

// non numerical default time, will be sorted to the back if the list
//const default_time = 'xxx:xx.xxx'
//const html_tooltip = true

/**
 * Generate an HTML tooltip for a row
 * @param {*} row 
 * @returns 
 */
/*
const getTooltip = row => {
  if ( !row.data ) return `<div class="tooltip empty">${row.car}</div>`
  // this is unsafe HTML!
  return `<div class="tooltip">${row.car}&nbsp;#${row.position}&nbsp;@${row.data.time}<br/><b>${row.data.laptime}</b></div>`
}
*/

/**
 * Render the position chart
 * @param {*} param0 
 * @returns 
 */
const ScatterChart = ( { rows = [], max_rows = 999, shorten = false, height = '100px', listColours = false } ) =>
{
  // get display options
  const display = useSelector( getDisplayOptions )
  const you = useSelector( getYou )
  // get fastest time per car, sorted on laptime
  const fastest = useSelector( getFastestPerCar )
  // fastest laps as marked in the lists, for listColours
  const fastestDay = useSelector( getFastestPerDay )
  const fastestGroup = useSelector( getFastestPerGroup )
  const showWeather = useSelector( getShowWeather )
  // extract row ids
  const fastest_ids = fastest.map( f => f.id )  
  // the lap of a clicked point
  const { ref, onClick, onChartReady, close, tooltip } = useChartTooltip()
  // get day graph setting
  const { day_graph = true } = display

  // stop righ away if user doesn't want to show the position graph
  if ( !day_graph ) return null

  // stop here if there's no data
  if ( !rows || rows.length === 1 ) return null

  // sort by time so oldest item is first
  rows.sort( (a , b) => a.time > b.time ? 1 : -1 )

  // maybe limit number of rows to show
  const sliced = shorten && max_rows && max_rows < rows.length ? rows.slice( -1 * Math.min( max_rows + 1, rows.length ) ) : rows

  if ( !sliced || sliced.length <= 1 ) return null

  // trend range; go this many steps before and after the current step and average the result
  // const trend_range = Math.min( 20, Math.min( 20, Math.round( sliced.length / 5 ) ) )
  const trend_range = Math.round( sliced.length / 10 ),
        sliced_max = sliced.length - 1
  
  // get data format
  let data = sliced.map( ( row, index ) => 
  {             
          // get ms values
    const laptime = laptime2ms( row.laptime ),
          // get trend range
          trend_start_raw = index - trend_range,
          trend_start = Math.max( 0, trend_start_raw),
          // when at the start, add the first value numerous time to bend the graph to the start point
          trend_start_offset = Math.abs( trend_start_raw - trend_start ),
          trend_start_accent = trend_start_offset * laptime2ms( sliced[ trend_start ].laptime ),
          trend_end_raw = index + trend_range, 
          trend_end = Math.min( sliced_max, trend_end_raw ), 
          // when at the end, add the last value numerous time to bend the graph to the end point
          trend_end_offset = Math.abs( trend_end_raw - trend_end ),
          trend_end_accent = trend_end_offset * laptime2ms( sliced[ trend_end ].laptime ),
          // get trend average
          trend_slice = sliced.slice( trend_start, trend_end ),
          trend_total = trend_slice.reduce( ( total, current ) => ( total + laptime2ms( current.laptime ) ), 0 ),
          trend_avg = parseInt( ( trend_total + trend_start_accent + trend_end_accent ) / ( trend_end - trend_start + trend_start_offset + trend_end_offset ) )  

          // dot colors, extracted from less
    const clr_blue = '#3366cc',
          clr_orange = '#ffa500',
          clr_red = '#ff0000',
          clr_green = '#008000',
          clr_purple = '#a500a5',
          // booleans
          is_you = row.car === parseInt( you ),
          is_invalid = row.penalty > 0 || row.comment !== '',
          is_fast = fastest_ids.includes( row.id ),
          is_fastest = fastest_ids[ 0 ] === row.id

    // let style = `color: ${clr_blue}; fill_color: ${clr_blue}; stroke-color: ${clr_blue};`;
    let style = {
      color: clr_blue,
      fill_color: clr_blue,
      stroke_color: clr_blue
    };

    if ( listColours )
    {
      // same colours as the lists: cones or fault, fastest of the day, fastest in the group
      const colour = getLapColour( row, fastestDay, fastestGroup, clr_blue )
      style = {
        color: colour,
        fill_color: colour,
        stroke_color: colour
      };
    }
    else if ( is_invalid )
    {
      style = {
        color: clr_orange,
        fill_color: clr_orange,
        stroke_color: clr_orange
      };
    }
    else if ( is_fastest )
    {
      style = {
        color: clr_purple,
        fill_color: clr_purple,
        stroke_color: clr_purple
      };
    }
    else if ( is_fast ) 
    {
      style = {
        color: clr_green,
        fill_color: clr_green,
        stroke_color: clr_green
      };
    }

    if ( is_you ) 
    {      
      style.stroke_color = clr_red;
    }

    const style_string = `color: ${style.color}; fill_color: ${style.fill_color}; stroke-color: ${style.stroke_color};`;

    // return the data row
    return [ 
      // x / h-axis = time of recording, in seconds so laps are spaced by time
      {
        v: time2seconds( row.time ),
        f: row.time
      },
      // y1 / v-axis = laptime
      {
        v: laptime,
        f: row.laptime
      },
      // style column
      style_string,
      // y2 / v-axis = average laptime / trend
      {
        v: trend_avg,
        f: ms2laptime( trend_avg )
      }
    ] 
  } )

  // add header row
  data.unshift( [ 
    // x colummn
    'Lap',    
    // y column 1
    'Laptime', 
    // styles for y column 1
    { role: 'style' }, 
    // y column 2; average laptime
    'Average'
  ] ) 

  // y-axis labels: fastest, average and slowest lap
  const laptimeAxis = getLaptimeAxis( sliced.map( row => laptime2ms( row.laptime ) ) )

  // the axis runs from the first to the last lap, so there's no empty space before or after them
  const first_time = time2seconds( sliced[ 0 ].time ),
        last_time = time2seconds( sliced[ sliced.length - 1 ].time )

  // chart area around a plot of the given height, with room for the weather above it
  const area = { left: LAPTIME_AXIS_WIDTH, right: 3, top: 4 + ( showWeather ? WEATHER_HEIGHT : 0 ), bottom: 25 }

  // a click near a point shows its lap; the first data column holds the laps, the second the trend
  const events = [ { eventName: 'ready', callback: onChartReady( ( row, column ) => column === 1 ? rows[ row ] : null ) } ]

  // render the chart, see https://www.react-google-charts.com for options
  return (
    <div className="chart scatter-chart" id="scatter-chart" onClick={ onClick } ref={ ref }>
      { tooltip }
      <ChartContainer
        onViewChange={ close }
        min={ first_time }
        max={ last_time }
        minRange={ 10 * 60 }
        left={ area.left }
        right={ area.right }
        footer={ showWeather ? <WeatherForecast credit/> : null }
      >
        { view => (
          <React.Fragment>
            { showWeather && (
              <WeatherStrip
                from={ first_time }
                to={ last_time }
                view={ view || { min: first_time, max: last_time } }
                left={ area.left }
                right={ area.right }
                top={ area.top }
                bottom={ area.bottom }
              />
            ) }
            <Chart 
              width={ '100%' }
              height={ ( parseInt( height ) + area.top + area.bottom ) + 'px' }
              chartType="ComboChart"
              
              options={ { 
                legend: 'none',
                tooltip: { 
                  trigger: 'none', 
                },
                series: {
                  0: {
                    pointSize: 4,
                    type: 'scatter'
                  },
                  1: {
                    pointSize: 0,
                    lineWidth: 2,
                    type: 'line',
                    curveType: 'function',
                  }
                },
                vAxis: { 
                  ...laptimeAxis,
                  gridlines: {
                    color: '#999'
                  }
                },
                hAxis: {
                  // the time below the plot, every hour or finer when zoomed in
                  ticks: getClockTicks( view ? view.min : first_time, view ? view.max : last_time ),
                  textStyle: { fontSize: 12 },
                  minValue: first_time,
                  maxValue: last_time,
                  viewWindow: view || undefined
                },
                chartArea: { 
                  left: area.left,
                  top: area.top, 
                  right: area.right,
                  bottom: area.bottom,
                },
              } }
              data={ data } 
              chartEvents={ events }
            />
          </React.Fragment>
        ) }
      </ChartContainer>
    </div>
  )
}

export default ScatterChart